import { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import {
  activeOddsLevel,
  MOD_LABELS,
  type IframePoolOfferOdds,
  type VolatilityLevel,
} from '../../../../modules/api';
import { OddsModal } from '../OddsModal';
import { StatsSummary } from './StatsSummary';
import { ViewOddsTrigger } from './ViewOddsTrigger';

export interface OddsPanelProps {
  odds: IframePoolOfferOdds;
  volatility: VolatilityLevel;
  onVolatilityChange: (level: VolatilityLevel) => void;
}

/**
 * Modern OddsPanel — pixel-perfect Figma `1336:86230`.
 *
 * Glass stats card: Min Value · Max Pull (centre hero) · View Odds. The
 * "View Odds" button opens the per-rarity breakdown in a centred DS modal
 * ({@link OddsModal}, Figma `1334:85344`) on every width. Desktop used to
 * anchor a popover above this block, but its fixed height overflowed under
 * the header whenever a short pack description left less room above
 * (Multi-Sport, 2026-10-15).
 *
 * Design Constructor surface name: `StatsRow`. Glass background / border /
 * blur flow through `--co-StatsRow-*` (see `index.css` `.stats-row-card`).
 */
export function OddsPanel({ odds, volatility, onVolatilityChange }: OddsPanelProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const level = activeOddsLevel(odds, volatility);
  const modLabel = MOD_LABELS[volatility];

  return (
    // OddsModal portals to <body>, so it needs no positioned wrapper here.
    <>
      <section
        aria-label="Pack odds"
        data-co-component="StatsRow"
        className="
          stats-row-card
          relative
          @[800px]:overflow-hidden
          @[800px]:[border-radius:var(--co-StatsRow-borderRadius,28px)]
          @[800px]:shadow-[0_64px_34px_0_rgba(0,0,0,0.05)]
          @[800px]:@max-[1200px]:[--statsRow-sizeScale:0.85]
        "
      >
        <StatsSummary
          className="px-5 py-4"
          minValue={level.minValue}
          maxPull={level.maxValue}
          rightSlot={
            <ViewOddsTrigger modLabel={modLabel} isOpen={open} onClick={() => setOpen((v) => !v)} />
          }
        />
      </section>

      <AnimatePresence>
        {open && (
          <OddsModal
            odds={odds}
            volatility={volatility}
            onVolatilityChange={onVolatilityChange}
            onClose={close}
          />
        )}
      </AnimatePresence>
    </>
  );
}
